import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Menu, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { NAV, PORTALS, FOOTER_COLUMNS } from "../../lib/nav";

// Every menu / footer label in one place; saved as site-wide ("*") text overrides.
const GROUPS = () => [
  ...NAV.map((n) => ({ title: `Menu · ${n.label}`, labels: [n.label, ...(n.items || []).map(([l]) => l)] })),
  { title: "My DKF portals", labels: PORTALS.map(([l]) => l) },
  ...FOOTER_COLUMNS.map((c) => ({ title: `Footer · ${c.title}`, labels: [c.title, ...c.links.map(([l]) => l)] })),
];

export default function NavLabelsEditor({ isSuper, onSaved }) {
  const [open, setOpen] = useState(false);
  const [live, setLive] = useState({});
  const [edits, setEdits] = useState({});
  const load = useCallback(() => api.get("/site-copy").then((r) => setLive(Object.fromEntries((r.data.items || []).filter((i) => i.path === "*" && i.kind === "text").map((i) => [i.original, i.value])))).catch(() => {}), []);
  useEffect(() => { if (open) load(); }, [open, load]);
  const save = async (original) => {
    const value = (edits[original] ?? "").trim();
    if (!value) return;
    try { await api.post("/admin/site-copy", { path: "*", kind: "text", original, value, publish: isSuper }); toast.success(isSuper ? "Published" : "Saved as draft — submit it for publishing below"); setEdits((e) => { const c = { ...e }; delete c[original]; return c; }); load(); onSaved?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  return (
    <div className="bg-white rounded-2xl border border-black/5 p-5" data-testid="nav-labels-editor">
      <button onClick={() => setOpen(!open)} className="font-semibold text-sm flex items-center gap-2 w-full text-left" data-testid="nav-labels-toggle"><Menu size={16} /> Menus & footer links <span className="ml-auto text-xs text-[#0a0d12]/60">{open ? "Hide" : "Rename items"}</span></button>
      {open && (
        <div className="mt-4 grid md:grid-cols-2 gap-5">
          {GROUPS().map((g) => (
            <div key={g.title}>
              <p className="text-[11px] uppercase tracking-wider text-[#0a0d12]/50 mb-2">{g.title}</p>
              <ul className="space-y-1.5">
                {[...new Set(g.labels)].map((l) => (
                  <li key={l} className="flex items-center gap-2 text-xs">
                    <span className="w-36 truncate text-[#0a0d12]/70" title={l}>{l}</span>
                    <input value={edits[l] ?? live[l] ?? ""} placeholder={l} onChange={(e) => setEdits({ ...edits, [l]: e.target.value })} className="flex-1 border rounded px-2 py-1" data-testid={`nav-label-${l.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`} />
                    {edits[l] !== undefined && edits[l].trim() && edits[l].trim() !== (live[l] || "") && <button onClick={() => save(l)} className="dk-btn-primary !py-1 !px-2 text-[11px]" data-testid={`nav-label-save-${l.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}><Save size={11} /> Save</button>}
                    {live[l] && edits[l] === undefined && <span className="text-[10px] text-green-800 font-bold">live</span>}
                  </li>))}
              </ul>
            </div>))}
          <p className="md:col-span-2 text-[11px] text-[#0a0d12]/55">Renaming changes the label only — links keep working. Restore the original from the list below at any time.</p>
        </div>)}
    </div>
  );
}
